export const LOW_CODE_POPUP_PAGE_ACTION = "openLowCodePopupPage";

const localeAliasMap = {
  "zh-CN": "zh-Hans",
  "zh-Hans": "zh-Hans",
  zh: "zh-Hans",
  hans: "zh-Hans",
  "zh-TW": "zh-Hant",
  "zh-Hant": "zh-Hant",
  hant: "zh-Hant",
  "vi-VN": "vi",
  vi_VN: "vi",
  "ja-JP": "ja",
  ja_JP: "ja",
  "th-TH": "th_TH",
  th_TH: "th_TH",
  th: "th_TH",
  "ms-MY": "ms_MY",
  ms_MY: "ms_MY",
  ms: "ms_MY",
};

export function isLowCodePopupPageAction(action) {
  return !!(
    action &&
    typeof action === "object" &&
    (action.type === LOW_CODE_POPUP_PAGE_ACTION ||
      action.responseType === LOW_CODE_POPUP_PAGE_ACTION)
  );
}

export function getLowCodePopupPageConfig(action) {
  if (!isLowCodePopupPageAction(action)) return null;
  if (action.value && typeof action.value === "object" && !Array.isArray(action.value)) {
    return action.value;
  }
  if (
    action.popupPageConfig &&
    typeof action.popupPageConfig === "object" &&
    !Array.isArray(action.popupPageConfig)
  ) {
    return action.popupPageConfig;
  }
  return action;
}

export function hasNativeLowCodePopupBridge(environment = globalThis) {
  if (!environment || !environment.window) return false;
  const currentWindow = environment.window;
  return !!(
    currentWindow.LowCodeNativeBridge?.openLowCodePopupPage ||
    currentWindow.AndroidLowCodeBridge?.openLowCodePopupPage ||
    currentWindow.webkit?.messageHandlers?.lowCodePopupPage?.postMessage ||
    currentWindow.ReactNativeWebView?.postMessage ||
    currentWindow.uni?.postMessage
  );
}

export function resolveLowCodeRuntimePlatform(context = {}) {
  const explicitPlatform =
    context?.platform ||
    context?.bindData?.common?.platform ||
    context?.bindData?.common?.runtimePlatform;
  if (["pc", "h5", "app"].includes(explicitPlatform)) {
    return explicitPlatform;
  }

  if (process.client && hasNativeLowCodePopupBridge(globalThis)) return "app";
  if (
    context?.bindData?.common?.isH5 ||
    context?.bindData?.common?.isMobile
  ) {
    return "h5";
  }

  if (process.client) {
    const queryPlatform = new URLSearchParams(window.location.search).get(
      "runtimePlatform"
    );
    if (["pc", "h5", "app"].includes(queryPlatform)) return queryPlatform;
    if (/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "")) {
      return "h5";
    }
  }
  return "pc";
}

export function getPlatformPopupConfig(config, platform) {
  const runtimeScene = platform === "pc" ? "pc" : "h5";
  if (config?.page && (!config.scene || config.scene === runtimeScene)) {
    return config.page;
  }
  const platforms = config?.platforms || {};
  if (platform === "app") return platforms.h5 || platforms.app || null;
  if (platforms[platform]) return platforms[platform];
  return null;
}

export function normalizePopupSize(size, fallback) {
  const fallbackSize = fallback || { value: 100, unit: "%" };
  const numericValue = Number(size?.value);
  const allowedUnits = new Set(["px", "%", "vh"]);
  const unit = allowedUnits.has(size?.unit) ? size.unit : fallbackSize.unit;
  let value = Number.isFinite(numericValue) && numericValue > 0
    ? numericValue
    : fallbackSize.value;
  if (unit === "%" || unit === "vh") value = Math.min(value, 100);
  return { value, unit };
}

export function popupSizeToCss(size, fallback) {
  const normalized = normalizePopupSize(size, fallback);
  return `${normalized.value}${normalized.unit}`;
}

export function resolvePopupTitle(config, context = {}, locale = "zh-CN") {
  const fallbackTitle = String(config?.title || "");
  const localeKey = localeAliasMap[locale] || locale;
  const localeCandidates = Array.from(
    new Set([
      locale,
      localeKey,
      String(locale).replace(/-/g, "_"),
      String(locale).split(/[-_]/)[0],
      localeKey === "zh-Hans" ? "hans" : "",
    ].filter(Boolean))
  );
  const titleI18n = config?.titleI18n || {};
  const parentLangJson = context?.langJson || {};
  const parentTitleI18n = parentLangJson[fallbackTitle] || {};
  const findTitle = (source) => {
    for (const key of localeCandidates) {
      if (source?.[key]) return source[key];
    }
    return "";
  };
  return (
    findTitle(titleI18n) ||
    findTitle(parentTitleI18n) ||
    parentTitleI18n["zh-Hans"] ||
    parentTitleI18n.hans ||
    titleI18n["zh-Hans"] ||
    fallbackTitle
  );
}

export function createLowCodePopupRequestId() {
  if (process.client && typeof crypto?.randomUUID === "function") {
    return `low_code_popup_${crypto.randomUUID()}`;
  }
  return `low_code_popup_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

export function invokeNativeLowCodePopup(payload) {
  if (!process.client) return false;
  const serializedPayload = JSON.stringify(payload);
  try {
    if (window.LowCodeNativeBridge?.openLowCodePopupPage) {
      window.LowCodeNativeBridge.openLowCodePopupPage(serializedPayload);
      return true;
    }
    if (window.AndroidLowCodeBridge?.openLowCodePopupPage) {
      window.AndroidLowCodeBridge.openLowCodePopupPage(serializedPayload);
      return true;
    }
    if (window.webkit?.messageHandlers?.lowCodePopupPage?.postMessage) {
      window.webkit.messageHandlers.lowCodePopupPage.postMessage(payload);
      return true;
    }
    if (window.ReactNativeWebView?.postMessage) {
      window.ReactNativeWebView.postMessage(serializedPayload);
      return true;
    }
    if (window.uni?.postMessage) {
      window.uni.postMessage({ data: payload });
      return true;
    }
  } catch (error) {
    console.error("open native low code popup page error", error);
  }
  return false;
}
